import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner, Badge, Pagination, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import AppConstants from '../../AppConstants';
import { useAuth } from '../../AuthProvider';
import './index.scss';

interface Service {
  _id: string;
  name: string;
  description?: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

interface ServiceFormData {
  name: string;
  description: string;
  category: string;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const ServiceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [formData, setFormData] = useState<any>({
    name: '',
    description: '',
    category: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});



  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Fetch services from API
  const fetchServices = async (page: number = 1, search: string = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        setLoading(false);
        return;
      }

      let url = `${AppConstants.ADMIN_SERVICES_API}?page=${page}&limit=${itemsPerPage}`;
      
      // Add search parameter if provided
      if (search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        const servicesData = response.data.data?.services || response.data.data || [];
        setServices(servicesData);
        
        // Set pagination data or create default pagination if services exist
        if (response.data.data?.pagination) {
          setPagination(response.data.data.pagination);
        } else if (Array.isArray(servicesData) && servicesData.length > 0) {
          // Fallback pagination for non-paginated response
          setPagination({
            currentPage: 1,
            totalPages: 1,
            totalItems: servicesData.length,
            hasNextPage: false,
            hasPrevPage: false
          });
        } else {
          setPagination(null);
        }
      } else {
        throw new Error(response.data.message || 'Failed to fetch services');
      }
    } catch (error: any) {
      console.error('Error fetching services:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      // Don't show alert for 401 errors as they trigger automatic logout
      if (error.response?.status !== 401) {
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           'Failed to load services';
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Main effect for fetching data
  useEffect(() => {
    fetchServices(currentPage, debouncedSearchTerm);
  }, [currentPage, debouncedSearchTerm]);

  // Initial load - removed since main useEffect handles it
  // useEffect(() => {
  //   const token = localStorage.getItem('authToken');
  //   if (token) {
  //     fetchServices();
  //   }
  // }, []);

  // Service categories - matching backend enum
  const SERVICE_CATEGORIES = [
    'Brakes',
    'Lights',
    'Tires',
    'Engine',
    'Transmission',
    'Electrical',
    'Body',
    'Suspension',
    'Fluids',
    'HVAC',
    'Exhaust',
    'Safety',
    'Steering',
    'Battery',
    'Cooling System',
    'General'
  ];

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: ''
    });
    setErrors({});
    setEditingService(null);
  };

  // Open modal for creating new service
  const handleCreateService = () => {
    resetForm();
    setShowModal(true);
  };

  // Open modal for editing service
  const handleEditService = (service: Service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      category: service.category || ''
    });
    setErrors({});
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: ServiceFormData) => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Service name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Service name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Service name cannot exceed 100 characters';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save service (create or update)
  const handleSaveService = async () => {
    if (!validateForm()) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('Authentication required. Please login again.');
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        category: formData.category
      };

      if (editingService) {
        // Update existing service
        const response = await axios.put(
          `${AppConstants.ADMIN_UPDATE_SERVICE_API}/${editingService._id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          toast.success('Service updated successfully!');
        } else {
          throw new Error(response.data.message || 'Failed to update service');
        }
      } else {
        // Create new service
        const response = await axios.post(
          AppConstants.ADMIN_CREATE_SERVICE_API,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          toast.success('Service created successfully!');
        } else {
          throw new Error(response.data.message || 'Failed to create service');
        }
      }

      handleCloseModal();
      fetchServices();
    } catch (error: any) {
      console.error('Error saving service:', error);
      console.error('Save error status:', error.response?.status);
      console.error('Save error data:', error.response?.data);
      
      let errorMessage = 'Failed to save service';
      
      // Don't show UI error for 401 as it triggers logout
      if (error.response?.status === 401) {
        console.error('Authentication error during save - user will be logged out');
        return;
      }
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Open delete confirmation modal
  const handleDeleteService = (service: Service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setServiceToDelete(null);
  };

  // Confirm delete service
  const handleConfirmDelete = async () => {
    if (!serviceToDelete) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('Authentication required. Please login again.');
      return;
    }

    setDeleting(true);
    try {
      const response = await axios.put(`${AppConstants.ADMIN_DELETE_SERVICE_API}/${serviceToDelete._id}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success('Service deleted successfully!');
        fetchServices();
        handleCloseDeleteModal();
      } else {
        throw new Error(response.data.message || 'Failed to delete service');
      }
    } catch (error: any) {
      console.error('Error deleting service:', error);
      const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           'Failed to delete service';
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    if (!pagination) return null;

    const items = [];
    const totalPages = pagination.totalPages || 1;
    const current = pagination.currentPage || 1;

    // First page
    items.push(
      <Pagination.First
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={current === 1}
      />
    );

    // Previous page
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(Math.max(1, current - 1))}
        disabled={current === 1}
      />
    );

    // Page numbers (show current page and 2 pages before/after)
    const startPage = Math.max(1, current - 2);
    const endPage = Math.min(totalPages, current + 2);

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === current}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    // Next page
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, current + 1))}
        disabled={current === totalPages}
      />
    );

    // Last page
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={current === totalPages}
      />
    );

    return items;
  };

  // Use services directly since filtering is done server-side
  const filteredServices = Array.isArray(services) ? services : [];

  return (
    <Container fluid className="p-0 service-management-page mb-5">
      {/* Filter and Search */}
      <Card className="filter-block p-1 mb-3 rounded shadow-sm border-0">
        <Card.Body>
          <Row className="justify-content-between">
            <Col lg={4} md={12} className="mb-3 mb-lg-0 filters">
              <div className="search-box">
                <span>🔍</span>
                <input
                  type="text"
                  placeholder="Search by name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </Col>
            <Col lg={8} md={12} className="filters">
              <div className="d-flex justify-content-end search-create">
                <Button 
                  variant="primary" 
                  onClick={handleCreateService}
                  disabled={loading}
                >
                  + Add New Service
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading services...</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead>
                <tr style={{ whiteSpace: "nowrap" }}>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.length > 0 ? (
                  filteredServices.map((service) => (
                    <tr key={service._id}>
                      <td>
                        <strong>{service.name}</strong>
                        {service.description && (
                          <div className="text-muted small mt-1">{service.description}</div>
                        )}
                      </td>
                      <td>
                        <Badge bg="info" className="text-white">
                          {service.category || 'N/A'}
                        </Badge>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="action-icon-btn edit"
                            onClick={() => handleEditService(service)}
                          >
                            ✏️
                          </button>
                          <button
                            className="action-icon-btn delete"
                            onClick={() => handleDeleteService(service)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center py-4">
                      {searchQuery ? (
                        `No services found matching "${searchQuery}"`
                      ) : (
                        "No services found. Create your first service!"
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalItems >= 0 ? (
            <div className="d-flex justify-content-between align-items-center mt-2 pagination-block">
              <div>
                {pagination.totalItems > 0 ? (
                  `Showing: ${(pagination.currentPage - 1) * itemsPerPage + 1} to ${Math.min(
                    pagination.currentPage * itemsPerPage,
                    pagination.totalItems
                  )} of ${pagination.totalItems}`
                ) : (
                  'No services found'
                )}
              </div>
              {pagination.totalItems > 0 && (
                <Pagination className="mb-0">
                  {renderPaginationItems()}
                </Pagination>
              )}
            </div>
          ) : null}
        </>
      )}

      {/* Service Form Modal */}
      <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingService ? 'Edit Service' : 'Add New Service'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Service Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                isInvalid={!!errors.name}
                placeholder="Enter service name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category *</Form.Label>
              <Form.Select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                isInvalid={!!errors.category}
              >
                <option value="">Select a category</option>
                {SERVICE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.category}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                isInvalid={!!errors.description}
                placeholder="Enter service description (optional)"
              />
              <Form.Control.Feedback type="invalid">
                {errors.description}
              </Form.Control.Feedback>
            </Form.Group>

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveService} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                {editingService ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              editingService ? 'Update Service' : 'Create Service'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      {/* Delete Confirmation Modal */}
      <div className={`modal ${showDeleteModal ? 'active' : ''}`} onClick={(e) => e.target === e.currentTarget && handleCloseDeleteModal()}>
        <div className="modal-content" style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <div className="modal-title">Confirm Delete</div>
            <button className="close-btn" onClick={handleCloseDeleteModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
              <p style={{ fontSize: '18px', fontWeight: '600', color: '#2d3436', marginBottom: '12px' }}>
                Are you sure you want to delete the service "{serviceToDelete?.name}"?
              </p>
              <p style={{ fontSize: '14px', color: '#636e72' }}>
                This action will mark the service as deleted. This cannot be undone.
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-large" onClick={handleCloseDeleteModal} disabled={deleting}>
              Cancel
            </button>
            <button className="btn btn-primary btn-large" onClick={handleConfirmDelete} disabled={deleting} style={{ background: '#FF6B6B' }}>
              <span>{deleting ? "⏳" : "🗑️"}</span>
              <span>{deleting ? "Deleting..." : "Delete"}</span>
            </button>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ServiceManagement;